import { AnimationTriggerMetadata, style, animate, transition, trigger, keyframes } from '@angular/animations';

const MOTE_ANIMATION_TIME: number = 500;

export class Animations {

    public static slideInOut(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('slideInOut', [
            transition(':enter', [
                style({ transform: "translateX(-100%)" }),
                animate(`${time}ms ease`, style({ transform: "translateX(0)" }))
            ]),
            transition(':leave', [
                style({ transform: "translateX(0)" }),
                animate(`${time}ms ease`, style({ transform: "translateX(-100%)" }))
            ])
        ]);
    }

    public static popIn(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('popIn', [
            transition(':enter', [   // :enter is alias to 'void => *'
                style({ opacity: 0 }),
                animate(time, keyframes([
                    style({ transform: "scale(.8)" }),
                    style({ transform: "scale(1.05)" }),
                    style({ transform: "scale(1)", opacity: 1 })
                ]))
            ]),
            transition(':leave', [   // :leave is alias to '* => void'
                animate(time, keyframes([
                    style({ transform: "scale(1)" }),
                    style({ transform: "scale(1.05)" }),
                    style({ transform: "scale(0)", opacity: 0 })
                ]))
            ])
        ]);
    }

    public static slide(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('slide', [
            transition(':enter', [
                style({ transform: "translateY({{position}}%)" }),
                animate(time, style({ transform: "translateY(0%)" })),
            ], { params: { position: "-100" } }),
            transition(':leave', [
                style({ transform: "translateY(0%)" }),
                animate(time, style({ transform: "translateY({{position}}%)" }))
            ], { params: { position: "-100" } })
        ]);
    }

    public static slideFromTop(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('slideFromTop', [
            transition(':enter', [
                style({ transform: "translateY(-100%)" }),
                animate(time, style({ transform: "translateY(0%)" }))
            ]),
            transition(':leave', [
                style({ transform: "translateY(0%)", position: "fixed", top: "0" }),
                animate(time, style({ transform: "translateY(-100%)" }))
            ])
        ]);
    }

    public static slideFromBottom(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('slideFromBottom', [
            transition(':enter', [
                style({ transform: "translateY(100%)" }),
                animate(time, style({ transform: "translateY(0%)" }))
            ]),
            transition(':leave', [
                style({ transform: "translateY(0%)", position: "fixed", bottom: "0" }),
                animate(time, style({ transform: "translateY(100%)" }))
            ])
        ]);
    }

    public static slideFromLeft(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('slideFromLeft', [
            transition(':enter', [
                style({ transform: "translateX(-100%)" }),
                animate(time, style({ transform: "translateX(0%)" }))
            ]),
            transition(':leave', [
                style({ transform: "translateX(0%)", position: "fixed", left: "0" }),
                animate(time, style({ transform: "translateX(-100%)" }))
            ])
        ]);
    }

    public static slideFromRight(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('slideFromRight', [
            transition(':enter', [
                style({ transform: "translateX(100%)" }),
                animate(time, style({ transform: "translateX(0%)" }))
            ]),
            transition(':leave', [
                style({ transform: "translateX(0%)", position: "fixed", right: "0" }),
                animate(time, style({ transform: "translateX(100%)" }))
            ])
        ]);
    }

    public static fadeIn(time: number = MOTE_ANIMATION_TIME, delay: number = 0): AnimationTriggerMetadata {
        return trigger('fadeIn', [
            transition(':enter', [   // :enter is alias to 'void => *'
                style({ opacity: 0 }),
                animate(`${time}ms ${delay}ms`, style({ opacity: 1 }))
            ]),
            transition(':leave', [   // :leave is alias to '* => void'
                style({ opacity: 1 }),
                animate(`${time}ms`, style({ opacity: 0 }))
            ])
        ]);
    }

    public static fadeInOut(timeIn: number = MOTE_ANIMATION_TIME, timeOut: number = MOTE_ANIMATION_TIME, delayIn: number = 0, delayOut: number = 0): AnimationTriggerMetadata {
        return trigger('fadeInOut', [
            transition(':enter', [   // :enter is alias to 'void => *'
                style({ opacity: 0 }),
                animate("{{ timeIn }}ms {{ delay }}ms", style({ opacity: 1 }))
            ], { params: { timeIn: timeIn, delay: delayIn } }),
            transition(':leave', [   // :leave is alias to '* => void'
                style({ opacity: 1 }),
                animate("{{ timeOut }}ms ease-in", style({ opacity: 0 }))
            ], { params: { timeOut: timeOut, delay: delayOut } })
        ]);
    }

    public static unlock(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('unlock', [
            transition(':enter', [   // :enter is alias to 'void => *'
                style({ opacity: 0 }),
                animate(time, keyframes([
                    style({ transform: "scale(0)" }),
                    style({ transform: "scale(2)", opacity: 1 }),
                    style({ transform: "scale(1)" })
                ]))
            ])
        ]);
    }

    public static shrinkVertical(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('shrinkOutVertical', [
            transition(':leave', [
                style({
                    "padding-top": 0,
                    "padding-bottom": 0,
                    opacity: 1,
                    overflow: "hidden",
                }),
                animate(time, keyframes([
                    style({ opacity: 0.3 }),
                    style({ height: 0, opacity: 0 })
                ]))
            ])
        ]);
    }

    public static shrinkHorizontal(time: number = MOTE_ANIMATION_TIME): AnimationTriggerMetadata {
        return trigger('shrinkOutHorizontal', [
            transition(':leave', [
                style({
                    "padding-left": 0,
                    "padding-right": 0,
                    opacity: 1,
                    overflow: "hidden",
                }),
                animate(time, keyframes([
                    style({ opacity: 0.3 }),
                    style({ width: 0, opacity: 0 })
                ]))
            ])
        ]);
    }

    public static shrinkExpandVertical(time: number = MOTE_ANIMATION_TIME, maxHeight: number): AnimationTriggerMetadata {
        return trigger('shrinkExpandVertical', [
            transition(':enter', [
                style({ "max-height": 0, overflow: "hidden" }),
                animate(time, keyframes([
                    style({ "max-height": maxHeight + 'px' })
                ]))
            ]),
            transition(':leave', [
                style({ "max-height": maxHeight + 'px' }),
                animate(time, keyframes([
                    style({ "max-height": 0 })
                ]))
            ])
        ]);
    }

    public static welcome(): Array<AnimationTriggerMetadata> {
        return [
            trigger('one', [
                transition(':enter', [
                    style({ transform: "translateY(20px) scale(1.2)", opacity: 0 }),
                    animate(2500, keyframes([
                        style({ transform: "translateY(20px) scale(1.2)", opacity: 0 }),
                        style({ transform: "translateY(0) scale(1)", opacity: 1 }),
                    ]))
                ])
            ]),
            trigger('two', [
                transition(':enter', [
                    style({ transform: "translateY(20px) scale(1.2)", opacity: 0 }),
                    animate('2s 2s ease-out', style({ transform: "translateY(0) scale(1)", opacity: 1 }))
                ])
            ]),
            trigger('three', [
                transition(':enter', [
                    style({ transform: "translateY(20px) scale(1.2)", opacity: 0 }),
                    animate('2s 4s ease-out', style({ transform: "translateY(0) scale(1)", opacity: 1 }))
                ])
            ]),
            trigger('powered', [
                transition(':enter', [
                    style({ opacity: 0, }),
                    animate('2s 4s', style({ opacity: 1 }))
                ])
            ]),
            trigger('footer', [
                transition(':enter', [
                    style({ opacity: 0 }),
                    animate('1s 6s', style({ opacity: 1 }))
                ])
            ]),
            trigger('slideFromTop', [
                transition(':enter', [
                    style({ transform: "translateY(-100%)" }),
                    animate(300, style({ transform: "translateY(0%)" }))
                ]),
                transition(':leave', [
                    style({ transform: "translateY(0%)" }),
                    animate(300, style({ transform: "translateY(-100%)" }))
                ])
            ])
        ];
    }
}
